import { Box, Stack, Typography } from "@mui/material";
import { PortfolioDates } from "../../../../shared/hooks/use-portfolio-service/PortfolioDates";
import { dateTimeConversionService } from "../../../../shared/services/DateTimeConversionService";
import { mathCalculationService } from "../../../../shared/services/MathCalculationService";
import { TooltipContentDivider } from "../../../../screens/shared/views";

type CustomDateTimePickerTooltipContentProps = {
    selectedPortfolioDates: PortfolioDates;
    isTripleCustomBalanceEnabled: boolean;
};

const tooltipDateFormat = "DD.MMM HH:mm";
const roundPrecision = 0;
const customDateValueColour = "#FFB8B8FF";

function getHoursDelta(from: Date | null, to: Date | null): number {
    if (from === null || to === null) {
        return 0;
    }

    return mathCalculationService.round(
        (to.getTime() - from.getTime()) / 1000 / 60 / 60,
        roundPrecision
    );
}

function DateRow({
    label,
    shift,
    date,
    colour,
}: {
    label: string;
    shift: string;
    date: Date | null;
    colour?: string;
}) {
    const textSx = {
        fontSize: "inherit",
        color: colour,
    };

    return (
        <>
            <Typography sx={textSx} textAlign="right">{label}</Typography>
            <Typography sx={textSx}>{shift}</Typography>
            <Typography sx={textSx} textAlign="right">
                {dateTimeConversionService.convertDateToCustomFormattedString(date, tooltipDateFormat)}
            </Typography>
        </>
    );
}

export function CustomDateTimePickerTooltipContent({
    selectedPortfolioDates,
    isTripleCustomBalanceEnabled,
}: CustomDateTimePickerTooltipContentProps) {
    const delta1 = getHoursDelta(selectedPortfolioDates.current, selectedPortfolioDates.custom1);
    const delta2 = getHoursDelta(selectedPortfolioDates.custom1, selectedPortfolioDates.custom2);
    const delta3 = getHoursDelta(selectedPortfolioDates.custom2, selectedPortfolioDates.custom3);
    const delta4 = getHoursDelta(selectedPortfolioDates.custom3, selectedPortfolioDates.nearestExpiration);

    return (
        <Stack>
            <Box>Set up date for custom balance profile</Box>
            <TooltipContentDivider />
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "60px 45px 95px",
                    columnGap: "10px",
                    rowGap: 0,
                    fontSize: "13px",
                    "& > :nth-of-type(3n+1)": {
                        textAlign: "left",
                    },
                    "& > :nth-of-type(3n+2)": {
                        textAlign: "right",
                    },
                    "& > :nth-of-type(3n)": {
                        textAlign: "center",
                    },
                }}
            >
                <Typography sx={{ fontSize: "inherit", fontWeight: "bold" }} textAlign="right">Line</Typography>
                <Typography sx={{ fontSize: "inherit", fontWeight: "bold" }}>Shift</Typography>
                <Typography sx={{ fontSize: "inherit", fontWeight: "bold" }} textAlign="right">Target date</Typography>

                <DateRow label="Current" shift="0H" date={selectedPortfolioDates.current} />
                <DateRow
                    label="Custom 1"
                    shift={`+${delta1}H`}
                    date={selectedPortfolioDates.custom1}
                    colour={customDateValueColour}
                />

                {isTripleCustomBalanceEnabled &&
                    <DateRow
                        label="Custom 2"
                        shift={`+${delta2}H`}
                        date={selectedPortfolioDates.custom2}
                        colour={customDateValueColour}
                    />
                }

                {isTripleCustomBalanceEnabled &&
                    <DateRow
                        label="Custom 3"
                        shift={`+${delta3}H`}
                        date={selectedPortfolioDates.custom3}
                        colour={customDateValueColour}
                    />
                }

                <DateRow
                    label="Expiration"
                    shift={`+${delta4}H`}
                    date={selectedPortfolioDates.nearestExpiration}
                />
            </Box>
        </Stack>
    );
}
